import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  useGetCourseSummaryQuery,
  useGetTopCoursesQuery,
  useGetCourseAnalyticsQuery,
  useGetCourseRetentionQuery,
  useGetCoursePerformanceQuery,
  type CourseAnalyticsParams,
  type CoursePerformanceParams,
  type TopCourse,
} from '@/entities/Dashboard';
import { CustomInput } from '@/shared/ui/Fields';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import SearchIcon from '@/shared/assets/icons/search.svg';
import TagIcon from '@/shared/assets/icons/dashboard/Tag.svg';
import SuitcaseIcon from '@/shared/assets/icons/dashboard/Suitcase.svg';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import { buildMetric } from '../../../lib/metrics';
import { formatGrade, formatPercentOrDash, formatFullName } from '../../../lib/rows';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { DashboardFilters, type DashboardFilterValue } from '../../components/DashboardFilters';
import { AnalyticsPagination, AnalyticsSubTabs } from '../../components/AnalyticsTable';
import { RetentionRateChart } from '../../components/RetentionRateChart';
import styles from '../../dashboard/Dashboard.module.scss';

const COURSE_TYPE_BADGES: Record<string, { label: string; abbr: string }> = {
  'masters-degree': { label: "Master's Degree", abbr: 'MD' },
  'pre-masters-degree': { label: "Pre-Master's Degree", abbr: 'PM' },
  'bachelors-degree': { label: "Bachelor's Degree", abbr: 'BD' },
  short: { label: 'Short course', abbr: 'SC' },
};

const getCourseTypeBadge = (type: string) =>
  COURSE_TYPE_BADGES[type] ?? COURSE_TYPE_BADGES['masters-degree'];

const COURSE_ANALYTICS_SUB_TABS = [
  { key: 'progress', label: 'Student Progress' },
  { key: 'retention', label: 'Retention Rate' },
];

const PAGE_SIZE = 8;

interface CourseStatisticsTabProps {
  onExport: () => void;
}

const TopCoursesTable = ({ courses }: { courses: TopCourse[] }) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <caption className="sr-only">Course completion rate</caption>
      <thead>
        <tr>
          <th scope="col">#</th>
          <th scope="col">Courses</th>
          <th scope="col">Completion Rate</th>
        </tr>
      </thead>
      <tbody>
        {courses.map((row, index) => (
          <tr key={row.courseId}>
            <td>{index + 1}</td>
            <td>
              <div className={styles.tableStudentCell}>
                <span className={styles.tableStudentName}>{row.title}</span>
                <span className={styles.tableStudentSub}>{row.completedStudents} completed</span>
              </div>
            </td>
            <td>
              <span className={styles.ratePill}>{Math.round(row.completionRate)}%</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CourseStatisticsTab = ({ onExport }: CourseStatisticsTabProps) => {
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [analyticsSubTab, setAnalyticsSubTab] = useState('progress');
  const [analyticsFilters, setAnalyticsFilters] = useState<DashboardFilterValue>({});
  const [analyticsSearch, setAnalyticsSearch] = useState('');
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const debouncedAnalyticsSearch = useDebounce(analyticsSearch.trim(), 500);

  const [perfFilters, setPerfFilters] = useState<DashboardFilterValue>({});
  const [perfSearch, setPerfSearch] = useState('');
  const [perfPage, setPerfPage] = useState(1);
  const debouncedPerfSearch = useDebounce(perfSearch.trim(), 500);
  const date = toDashboardDateParam(datePeriod);

  const { data: summary } = useGetCourseSummaryQuery({ date });
  const { data: top } = useGetTopCoursesQuery({ date });

  const analyticsParams = useMemo<CourseAnalyticsParams>(
    () => ({
      intakeId: analyticsFilters.intakeId as number,
      page: analyticsPage,
      pageSize: PAGE_SIZE,
      search: debouncedAnalyticsSearch || undefined,
      termId: analyticsFilters.termId,
      groupId: analyticsFilters.groupId,
    }),
    [analyticsFilters, analyticsPage, debouncedAnalyticsSearch],
  );

  const { data: analytics } = useGetCourseAnalyticsQuery(analyticsParams, {
    skip: !analyticsFilters.intakeId,
  });
  const { data: retention } = useGetCourseRetentionQuery(
    { intakeId: analyticsFilters.intakeId as number },
    { skip: !analyticsFilters.intakeId || analyticsSubTab !== 'retention' },
  );

  const perfParams = useMemo<CoursePerformanceParams>(
    () => ({
      page: perfPage,
      pageSize: PAGE_SIZE,
      search: debouncedPerfSearch || undefined,
      type: perfFilters.type,
    }),
    [perfPage, debouncedPerfSearch, perfFilters.type],
  );

  const { data: performance } = useGetCoursePerformanceQuery(perfParams);

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      buildMetric('1', 'Total Courses', s?.totalCourses, 'count'),
      buildMetric('2', 'Active Courses', s?.activeCourses, 'count'),
      buildMetric('3', 'Avg. Completion Rate', s?.averageCompletionRate, 'percent'),
      buildMetric('4', 'Predicted Completion Rate', s?.coursePredictedCompletionRate, 'percent'),
    ];
  }, [summary]);

  const topCourseGroups = [
    { type: 'masters-degree', courses: top?.data?.mastersDegree ?? [] },
    { type: 'short', courses: top?.data?.short ?? [] },
    { type: 'pre-masters-degree', courses: top?.data?.preMastersDegree ?? [] },
    { type: 'bachelors-degree', courses: top?.data?.bachelorsDegree ?? [] },
  ];

  const analyticsCourse = analytics?.data?.course;
  const analyticsIntake = analytics?.data?.intake;
  const analyticsStudents = analytics?.data?.students ?? [];
  const analyticsPagination = analytics?.data?.pagination;
  const totalTerms = analytics?.data?.terms?.length ?? 0;

  const perfRows = performance?.data?.courses ?? [];
  const perfPagination = performance?.data?.pagination;

  const startedLabel = analyticsIntake?.startDate
    ? `started ${format(new Date(analyticsIntake.startDate), 'd MMMM yyyy')}`
    : '';

  const handleAnalyticsFilters = (value: DashboardFilterValue) => {
    setAnalyticsFilters(value);
    setAnalyticsPage(1);
  };

  const handlePerfFilters = (value: DashboardFilterValue) => {
    setPerfFilters(value);
    setPerfPage(1);
  };

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Courses Statistics"
          icon={<Svg Icon={SuitcaseIcon} width={20} height={20} className="text-[#0068D6]" />}
          iconBg="#E8F0FF"
          onExport={onExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <AnalyticsMetricsGrid metrics={metrics} />
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Top Courses"
          icon={<Svg Icon={TagIcon} width={20} height={20} className="text-[#5B3FD1]" />}
          iconBg="#F0EBFF"
          showToolbar={false}
        />
        <div className={styles.twoColumns}>
          {topCourseGroups.map((group) => (
            <div key={group.type}>
              <span className={group.type === 'short' ? styles.badgeSc : styles.badgeMd}>
                {getCourseTypeBadge(group.type).label}
              </span>
              <TopCoursesTable courses={group.courses} />
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Course Analytics"
          icon={<Svg Icon={SuitcaseIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          showToolbar={false}
        />
        <DashboardFilters value={analyticsFilters} onChange={handleAnalyticsFilters} showTerm />

        {!analyticsFilters.intakeId ? (
          <p className="text-sm text-grey-secondary">Select a course and intake to see analytics.</p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {analyticsCourse ? (
                <span className={analyticsCourse.type === 'short' ? styles.badgeSc : styles.badgeMd}>
                  {getCourseTypeBadge(analyticsCourse.type).label}
                </span>
              ) : null}
              <h3 className="text-lg font-semibold">{analyticsCourse?.title ?? '—'}</h3>
              <p className="text-sm text-grey-secondary">{startedLabel}</p>
            </div>
            <AnalyticsSubTabs
              tabs={COURSE_ANALYTICS_SUB_TABS}
              value={analyticsSubTab}
              onChange={setAnalyticsSubTab}
            />
            {analyticsSubTab === 'progress' ? (
              <>
                <CustomInput aria-label="Search"
                  className={searchInputClassName}
                  startContent={<Svg Icon={SearchIcon} />}
                  placeholder="Search"
                  value={analyticsSearch}
                  onValueChange={(v) => {
                    setAnalyticsSearch(v);
                    setAnalyticsPage(1);
                  }}
                />
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <caption className="sr-only">Student progress in the course</caption>
                    <thead>
                      <tr>
                        <th scope="col">ID</th>
                        <th scope="col">Students</th>
                        <th scope="col">Student Progress</th>
                        <th scope="col">Current progression</th>
                        <th scope="col">Attendance</th>
                        <th scope="col">Engagement</th>
                        <th scope="col">Avg. Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsStudents.map((row) => (
                        <tr key={row.enrollmentId}>
                          <td>{row.enrollmentId}</td>
                          <td>
                            <div className={styles.tableStudentCell}>
                              <span className={styles.tableStudentName}>
                                {formatFullName(row.user)}
                              </span>
                            </div>
                          </td>
                          <td>
                            {row.courseCompletedModulesCount}/{totalTerms} terms
                          </td>
                          <td>
                            <div className={styles.progressCell}>
                              <span>
                                {formatPercentOrDash(row.courseProgressByModules)} modules
                              </span>
                              <span className={styles.progressSub}>
                                {formatPercentOrDash(row.courseProgressByLessons)} lessons
                              </span>
                            </div>
                          </td>
                          <td>{formatPercentOrDash(row.courseAttendance)}</td>
                          <td>{formatPercentOrDash(row.courseEngagement)}</td>
                          <td>{formatGrade(row.courseAverageGrade)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <AnalyticsPagination
                  page={analyticsPagination?.page ?? analyticsPage}
                  pageCount={analyticsPagination?.pageCount ?? 0}
                  onPageChange={setAnalyticsPage}
                />
              </>
            ) : (
              <RetentionRateChart
                terms={retention?.data?.terms}
                avgRate={retention?.data?.intake?.avgRate}
              />
            )}
          </>
        )}
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Course Performance Overview"
          icon={<Svg Icon={TagIcon} width={20} height={20} className="text-[#5B3FD1]" />}
          iconBg="#F0EBFF"
          showToolbar={false}
        />
        <CustomInput aria-label="Search"
          className={searchInputClassName}
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={perfSearch}
          onValueChange={(v) => {
            setPerfSearch(v);
            setPerfPage(1);
          }}
        />
        <DashboardFilters
          value={perfFilters}
          onChange={handlePerfFilters}
          showGroup={false}
        />
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className="sr-only">Course statistics</caption>
            <thead>
              <tr>
                <th scope="col">Courses</th>
                <th scope="col">Type</th>
                <th scope="col">Format</th>
                <th scope="col">Students</th>
                <th scope="col">Engagement</th>
                <th scope="col">Retention course</th>
                <th scope="col">Academic progress</th>
                <th scope="col">Avg. Completion rate</th>
                <th scope="col">Avg. Score</th>
                <th scope="col">Attendance</th>
              </tr>
            </thead>
            <tbody>
              {perfRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.title}</td>
                  <td>
                    <span className={row.type === 'short' ? styles.badgeSc : styles.badgeMd}>
                      {getCourseTypeBadge(row.type).abbr}
                    </span>
                  </td>
                  <td>—</td>
                  <td>{row.studentCount}</td>
                  <td>0%</td>
                  <td>{formatPercentOrDash(row.retentionRate)}</td>
                  <td>
                    <div className={styles.progressCell}>
                      <span>{formatPercentOrDash(row.avgProgressByModules)} modules</span>
                      <span className={styles.progressSub}>
                        {formatPercentOrDash(row.avgProgressByLessons)} lessons
                      </span>
                    </div>
                  </td>
                  <td>{formatPercentOrDash(row.avgCompletionRate)}</td>
                  <td>{formatGrade(row.avgScore)}</td>
                  <td>{formatPercentOrDash(row.attendance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AnalyticsPagination
          page={perfPagination?.page ?? perfPage}
          pageCount={perfPagination?.pageCount ?? 0}
          onPageChange={setPerfPage}
        />
      </section>
    </div>
  );
};
